import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _extends from '@babel/runtime/helpers/extends';
import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Transition } from 'react-transition-group';
import throttle from 'lodash.throttle';
import { FocusScope, useFocusManager } from '@react-aria/focus';
import { useDialog } from '@react-aria/dialog';
import { REACT_PORTAL_SECTION_ID } from '../../constants/portals/index.js';
import { OffClickWrapper } from '../offClickWrapper/index.js';
import { jsx } from '@emotion/react';
import '../../constants/breakpoints/index.js';
import '../../constants/grid/index.js';
import '../../constants/mediaQueries/index.js';
import '../../constants/boxShadows/primary.js';
import '../../constants/boxShadows/secondary.js';
import CrossIcon from '../../icons/icons/crossIcon.js';
import Style from './style.js';
import { isDefined } from '../../utils/isDefined/index.js';

var _excluded = ["children", "footerContent", "headerImage", "onClose", "title"];
var MODAL_MOBILE_SCROLLING_ID = 'modal-mobile-scrolling-id';
var MODAL_DESKTOP_SCROLLING_ID = 'modal-desktop-scrolling-id';

var getHtmlNode = () => {
  var _document$querySelect;

  return (_document$querySelect = document.querySelector('html')) !== null && _document$querySelect !== void 0 ? _document$querySelect : document.body;
};

var getDomNode = () => {
  var _document$getElementB;

  return (_document$getElementB = document.getElementById(REACT_PORTAL_SECTION_ID)) !== null && _document$getElementB !== void 0 ? _document$getElementB : document.body;
};

var getModalMobileScrollingElement = () => document.getElementById(MODAL_MOBILE_SCROLLING_ID);

var getModalDesktopScrollingElement = () => document.getElementById(MODAL_DESKTOP_SCROLLING_ID);
/**
 * We define a standalone component, ImmersiveModalContent, in order to add
 * `useFocusManager` functionality, which only works as a child of <FocusScope>
 */


var ImmersiveModalContent = _ref => {
  var {
    footerContent,
    headerImage,
    showDesktopHeaderBar,
    showMobileHeaderBar,
    title,
    handleCloseIntent,
    children
  } = _ref;
  var ref = React.useRef(null);
  var focusManager = useFocusManager();
  /**
   * Exposes ARIA dialog role to assistive technology, i.e., announces to users with
   * screen readers that they are in a dialog component, thereby making apparent
   * additional accessibility functionality (e.g., exiting dialog with "Esc" key)
   */

  var {
    dialogProps,
    titleProps
  } = useDialog({
    role: 'dialog'
  }, ref);
  /**
   * It is not typical modal behavior to be able to scroll. Consequently, our
   * keyboard autofocus handling means when users try and scroll in the ImmersiveModal,
   * nothing happens. We make the content of the container tabbable such that when the
   * arrow keys are used (as in normal scroll behavior) we are able to break focus with
   * the close button and allow scrolling of the content
   */

  var onKeyDown = event => {
    switch (event.key) {
      case 'ArrowDown':
        focusManager.focusNext({
          wrap: true
        });
        break;

      case 'ArrowUp':
        focusManager.focusPrevious({
          wrap: true
        });
        break;
    }
  };

  var hasHeaderImage = isDefined(headerImage);
  return jsx(Style.MainModalContentContainer, _extends({
    id: MODAL_DESKTOP_SCROLLING_ID,
    hasHeaderImage: hasHeaderImage,
    ref: ref
  }, dialogProps), !showMobileHeaderBar && jsx(React.Fragment, null, jsx(Style.DesktopHeaderBar, _extends({}, titleProps, {
    showDesktopHeaderBar: showDesktopHeaderBar
  }), jsx("span", null, title)), jsx(Style.CrossIconButton, {
    onClick: handleCloseIntent,
    showDesktopHeaderBar: showDesktopHeaderBar,
    onKeyDown: onKeyDown,
    "aria-label": "Close modal"
  }, jsx(CrossIcon, null))), hasHeaderImage && jsx(Style.HeaderImageContainer, null, headerImage), jsx(Style.ContentWithFooterContainer, {
    hasHeaderImage: hasHeaderImage,
    tabIndex: 0
  }, jsx("div", null, isDefined(title) && jsx(Style.ModalTitle, null, title), jsx("section", null, children)), isDefined(footerContent) && jsx(Style.ModalFooter, null, footerContent)));
};
/**
 * It is used to provide a layer on top of a page when we need to present more content and actions to patients.
 *
 * It should not be used as a step to add friction / confirm an action, use `Dialog Modal` instead.
 *
 * They are closable if clicked outside of container.
 *
 * Immersive modals always include the close button.
 *
 * Modals can contain a header image that is 240px tall (264px on desktop). Images should not contain rounded corners.
 *
 * `ImmersiveModal.Paragraph` subcomponent may be used to add some margin to the paragraphs inside the modal body.
 */


var ImmersiveModal = _ref2 => {
  var {
    children,
    footerContent = null,
    headerImage = null,
    onClose,
    title = ''
  } = _ref2,
      rest = _objectWithoutProperties(_ref2, _excluded);

  var [isClosing, setIsClosing] = useState(false);
  var [showMobileHeaderBar, setShowMobileHeaderBar] = useState(false);
  var [showDesktopHeaderBar, setShowDesktopHeaderBar] = useState(false);
  var htmlNode = useRef(getHtmlNode());
  var domNode = useRef(getDomNode());
  var modalMobileScrollingElement = useRef(getModalMobileScrollingElement());
  var modalDesktopScrollingElement = useRef(getModalDesktopScrollingElement());
  var handleScroll = throttle(() => {
    if (modalMobileScrollingElement.current) {
      var shouldShowMobileHeaderBar = modalMobileScrollingElement.current.scrollTop > 32;
      setShowMobileHeaderBar(shouldShowMobileHeaderBar);
    }

    if (modalDesktopScrollingElement.current) {
      var shouldShowDesktopHeaderBar = modalDesktopScrollingElement.current.scrollTop > 32;
      setShowDesktopHeaderBar(shouldShowDesktopHeaderBar);
    }
  }, 100);

  var handleCloseIntent = () => {
    if (isClosing) {
      return;
    }

    setIsClosing(true);
    setShowMobileHeaderBar(false);
    setTimeout(onClose, 450);
  };

  useEffect(() => {
    htmlNode.current = getHtmlNode();
    domNode.current = getDomNode();
    modalMobileScrollingElement.current = getModalMobileScrollingElement();
    modalDesktopScrollingElement.current = getModalDesktopScrollingElement();
    var htmlNodeInstance = htmlNode.current;
    var modalMobileScrollingElementInstance = modalMobileScrollingElement.current;
    var modalDesktopScrollingElementInstance = modalDesktopScrollingElement.current;
    htmlNodeInstance.classList.add('no-scroll');

    if (modalMobileScrollingElementInstance && modalDesktopScrollingElementInstance) {
      modalMobileScrollingElementInstance.addEventListener('scroll', handleScroll);
      modalDesktopScrollingElementInstance.addEventListener('scroll', handleScroll);
    }

    return () => {
      htmlNodeInstance.classList.remove('no-scroll');

      if (modalMobileScrollingElementInstance && modalDesktopScrollingElementInstance) {
        modalMobileScrollingElementInstance.removeEventListener('scroll', handleScroll);
        modalDesktopScrollingElementInstance.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  return /*#__PURE__*/ReactDOM.createPortal(jsx(Transition, {
    timeout: {
      appear: 0,
      enter: 0,
      exit: 350
    },
    in: !isClosing,
    unmountOnExit: true,
    appear: true
  }, transitionState => jsx(React.Fragment, null, jsx(Style.MobileHeaderBar, {
    showMobileHeaderBar: showMobileHeaderBar
  }, title, jsx(Style.CrossIconButton, {
    onClick: handleCloseIntent,
    "aria-label": "Close modal"
  }, jsx(CrossIcon, null))), jsx(Style.Overlay, _extends({
    className: transitionState
  }, rest), jsx(Style.ModalContainer, {
    className: transitionState,
    id: MODAL_MOBILE_SCROLLING_ID
  }, jsx(OffClickWrapper, {
    onOffClick: handleCloseIntent,
    className: "modal-offclick-wrapper"
  }, jsx(Style.MobileTopOverlay, {
    onClick: handleCloseIntent
  }), jsx(FocusScope, {
    contain: true,
    restoreFocus: true,
    autoFocus: true
  }, jsx(ImmersiveModalContent, {
    footerContent: footerContent,
    headerImage: headerImage,
    showDesktopHeaderBar: showDesktopHeaderBar,
    showMobileHeaderBar: showMobileHeaderBar,
    title: title,
    handleCloseIntent: handleCloseIntent
  }, children))))))), domNode.current);
};
ImmersiveModal.Paragraph = Style.Paragraph;

export { ImmersiveModal };
